import React, { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from './overlay/Tooltip';

// Source value catalog — single source of truth.
// Backend canonical strings: user_input, market_live, reference, ai_extracted.
const SOURCE_CATALOG = {
  user_input: {
    icon: '✏️',
    labelKey: 'source.userInput',
    labelFallback: 'Eigene Angabe',
    palette: {
      pillBg: 'bg-blue-500/15 dark:bg-blue-500/20',
      pillText: 'text-blue-700 dark:text-blue-400',
      iconText: 'text-blue-600 dark:text-blue-400',
    },
  },
  market_live: {
    icon: '📈',
    labelKey: 'source.marketLive',
    labelFallback: 'Live-Marktpreis',
    palette: {
      pillBg: 'bg-green-500/15 dark:bg-green-500/20',
      pillText: 'text-green-700 dark:text-green-400',
      iconText: 'text-green-600 dark:text-green-400',
    },
  },
  reference: {
    icon: '📊',
    labelKey: 'source.reference',
    labelFallback: 'KTBL-Referenz',
    palette: {
      pillBg: 'bg-[var(--aif-bg-tertiary)] dark:bg-gray-800',
      pillText: 'text-[var(--aif-text-muted)]',
      iconText: 'text-[var(--aif-text-muted)]',
    },
  },
  ai_extracted: {
    icon: '🤖',
    labelKey: 'source.aiExtracted',
    labelFallback: 'KI-erkannt',
    palette: {
      pillBg: 'bg-amber-500/15 dark:bg-amber-500/20',
      pillText: 'text-amber-700 dark:text-amber-400',
      iconText: 'text-amber-600 dark:text-amber-400',
    },
  },
};

// Aliases — alternative source strings that map to canonical entries.
const SOURCE_ALIASES = {
  ai: 'ai_extracted',
  user: 'user_input',
  market: 'market_live',
  ktbl: 'reference',
  // A3: A7 data_source enum → SourceBadge type mapping (T1/T2/T3 bridge)
  verified_external: 'market_live',  // T1 official authority
  reference_data: 'reference',       // T2 aggregator/estimate
  ai_suggested: 'ai_extracted',      // T3 AI-generated
  inferred: 'ai_extracted',          // T3 system-computed
  imported: 'reference',             // T2 file import
  sensor: 'market_live',             // T1 sensor/IoT
};

function resolveSource(source) {
  if (!source) return null;
  if (SOURCE_CATALOG[source]) return source;
  if (SOURCE_ALIASES[source]) return SOURCE_ALIASES[source];
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`[SourceBadge] Unknown source value: "${source}". Falling back to 'reference'.`);
  }
  return 'reference';
}

/**
 * SourceBadge — visual indicator of a value's data origin.
 *
 * Variants:
 *   - pill (default): rounded-full pill with icon + label, uppercase tracking
 *   - compact: small icon-only badge with Radix Tooltip on hover/touch
 *   - inline: bare emoji span, native title attribute
 *
 * Props:
 *   - source: canonical string or alias
 *   - variant: 'pill' | 'compact' | 'inline' (default: 'pill')
 *   - hideReference: when true, returns null for source='reference' (default: false)
 *   - className: additional classes appended to the rendered element
 */
const SourceBadge = forwardRef(function SourceBadge(
  { source, variant = 'pill', hideReference = false, className = '', ...rest },
  ref
) {
  const { t } = useTranslation();
  const resolved = useMemo(() => resolveSource(source), [source]);

  if (!resolved) return null;
  if (hideReference && resolved === 'reference') return null;

  const meta = SOURCE_CATALOG[resolved];
  const label = t(meta.labelKey, { defaultValue: meta.labelFallback });

  // ---- Variant: inline ----
  if (variant === 'inline') {
    return (
      <span
        ref={ref}
        className={`text-[9px] ${meta.palette.iconText} ${className}`}
        title={label}
        role="img"
        aria-label={label}
        data-testid="source-badge"
        data-source={resolved}
        data-variant="inline"
        {...rest}
      >
        {meta.icon}
      </span>
    );
  }

  // ---- Variant: compact (icon + Radix tooltip) ----
  if (variant === 'compact') {
    return (
      <Tooltip label={label}>
        <span
          ref={ref}
          className={
            'inline-flex items-center justify-center text-[10px] px-1 py-0.5 rounded ' +
            'bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] ' +
            `${meta.palette.iconText} ${className}`
          }
          role="img"
          aria-label={label}
          data-testid="source-badge"
          data-source={resolved}
          data-variant="compact"
          {...rest}
        >
          {meta.icon}
        </span>
      </Tooltip>
    );
  }

  // ---- Variant: pill (default) ----
  return (
    <span
      ref={ref}
      className={
        `inline-flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 ` +
        `rounded-full uppercase tracking-wide ` +
        `${meta.palette.pillBg} ${meta.palette.pillText} ${className}`
      }
      data-testid="source-badge"
      data-source={resolved}
      data-variant="pill"
      {...rest}
    >
      <span aria-hidden="true">{meta.icon}</span>
      <span>{label}</span>
    </span>
  );
});

export default SourceBadge;

// Internal exports for unit testing
export const _internal = {
  SOURCE_CATALOG,
  SOURCE_ALIASES,
  resolveSource,
};
